import { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';

import {
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

import Logo from '@/assets/svg/logos/primary.svg';

interface ILayoutStandard {
  heading: string;
  description?: string;
  children: React.ReactNode;
  backgroundColor?: 'bg-white' | 'bg-gray-50';
}

const LayoutStandard: React.FC<ILayoutStandard> = props => {
  const {
    children,
    heading,
    description,
    backgroundColor = 'bg-white',
  } = props;

  return (
    <div
      className={clsx(
        'min-h-screen flex flex-col justify-center items-center py-6 sm:px-6 lg:px-8',
        backgroundColor,
      )}
    >
      <div className="w-full">
        <div className="sm:mx-auto sm:w-full sm:max-w-md lg:max-w-lg mb-8">
          <Link to="/">
            <img className="mx-auto h-24 w-24" src={Logo} alt="Tapt" />
          </Link>
          <h2 className="mt-2 text-center text-2xl leading-8 font-medium text-gray-900">
            {heading}
            {import.meta.env.VITE_DISPLAY_ENV
              ? ` (${import.meta.env.VITE_DISPLAY_ENV})`
              : ``}
          </h2>
          {description && (
            <p className="mt-8 px-4 text-center leading-6 text-gray-900">
              {description}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export { LayoutStandard as default };

type LayoutStandardWithSidebarProps = {
  heading: string;
} & PropsWithChildren;

export function LayoutStandardWithSidebar({
  children,
  heading,
}: LayoutStandardWithSidebarProps) {
  const bottom = [
    {
      name: 'Help',
      href: 'https://help.tapt.io/en',
      icon: <QuestionMarkCircleIcon className="w-5 h-5" />,
    },
    {
      name: 'Privacy Policy',
      href: '/privacy-policy',
      icon: <ShieldCheckIcon className="w-5 h-5" />,
    },
    {
      name: 'Terms',
      href: '/terms-of-service',
      icon: <DocumentTextIcon className="w-5 h-5" />,
    },
  ];

  return (
    <div className="flex flex-col">
      <div className="min-h-[100dvh] flex overflow-hidden bg-white">
        <div className="hidden xl:flex flex-shrink-0 border-r border-gray-200">
          <div className="relative flex-1 flex flex-col w-[235px] h-[100dvh] bg-white">
            <div className="flex-shrink-0 inline-flex items-center h-20 px-3">
              <Link to="/">
                <img className="h-8 w-auto" src={Logo} alt="Tapt" />
              </Link>
              {!!import.meta.env.VITE_DISPLAY_ENV &&
                `(${import.meta.env.VITE_DISPLAY_ENV})`}
            </div>
            <div className="flex-1" />
            <div className="divide-y divide-gray-300 border-y border-gray-300">
              <div className="p-3">
                {bottom.map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    className="flex items-center space-x-2 pl-3 py-2 text-gray-500 group"
                  >
                    <span className="">{item.icon}</span>
                    <span className="text-xs group-hover:text-brand-500">
                      {item.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-8 py-12 w-full">
          <h2 className="text-4xl font-medium text-gray-900">{heading}</h2>
          {children}
        </div>
      </div>
      <div className="xl:hidden flex justify-center space-x-4 bg-gray-50 py-4">
        {bottom.map((item, index) => (
          <a
            key={index}
            className="text-gray-500 leading-6 hover:text-gray-700 font-normal"
            href={item.href}
          >
            {item.name}
          </a>
        ))}
      </div>
    </div>
  );
}
